import type { AslenClient } from '../db';
import { unwrap } from './errors';

export type Bucket = 'documents' | 'labels' | 'product-images' | 'imports';

function safeName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .slice(-80);
}

/**
 * Path convention (enforced by storage RLS): <company_id>/<entity>/<uuid>-<filename>
 *
 * `id` is defaulted from the browser's `crypto`, which is the one platform that
 * has it: React Native ships no Web Crypto (Expo reaches for its own native uuid
 * for exactly this reason), so a caller that can run on the phone has to pass one
 * — from expo-crypto — or this line throws before anything is uploaded.
 */
export function objectPath(companyId: string, entity: string, fileName: string, id: string = crypto.randomUUID()): string {
  return `${companyId}/${entity}/${id}-${safeName(fileName)}`;
}

export async function uploadFile(
  client: AslenClient,
  bucket: Bucket,
  path: string,
  body: Blob | ArrayBuffer | Uint8Array,
  contentType: string,
): Promise<string> {
  unwrap(await client.storage.from(bucket).upload(path, body, { contentType, upsert: false }));
  return path;
}

/** Signed URL valid for one hour (documents are never public). */
export async function signedUrl(client: AslenClient, bucket: Bucket, path: string, seconds = 3600): Promise<string> {
  const data = unwrap(await client.storage.from(bucket).createSignedUrl(path, seconds));
  return data.signedUrl;
}
